import { Component, ElementRef, EventEmitter, Input, OnInit, ViewChild } from '@angular/core';
import { AppInfoService } from 'core/app-info/app-info.service';
import { AppLocalization } from 'src/app/modules/shared/entities/app-localization';
import { DashboardTemplateValues } from 'src/app/modules/shared/entities/dashboard-template-values.class';
import { AccountManager } from 'src/app/modules/shared/entities/account-manager.class';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatSelectChange } from '@angular/material/select';
import { PaginationPageEvent } from '@snapon/design-system-angular';
import { DialogData } from 'core/dialog/dialog-data.class';
import { DialogService } from 'core/dialog/dialog.service';
import { MatDialog } from '@angular/material/dialog';
import { Sort } from '@angular/material/sort';
import { SortDirection, SortLeads } from 'src/app/modules/pos/quotes/entities/sort.enum';
import { UtilsService } from 'core/utils/utils.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { FormBuilder } from '@angular/forms';
import { DashboardService } from '../../services/dashboard.service';
import { Lead } from '../../entities/lead';
import { NotesWidgetService } from '../../notes-widget/notes-widget.service';
import { LeadStatus } from './../../../../shared/entities/lead-status.enum';
import { EditLeadDialogComponent } from './edit-lead-dialog/edit-lead-dialog.component';

@Component({
  selector: 'app-leads-table',
  templateUrl: './leads-table.component.html',
  styleUrls: ['./leads-table.component.scss'],
  standalone: false
})
export class LeadsTableComponent implements OnInit {
  @Input() isHistory: boolean;
  @ViewChild('leadsTable') leadsTable: ElementRef;

  appStrings: AppLocalization;
  dateFormat: string;
  leadDashboard = '/accounts-leads/';
  displayedColumns = [
    'leadOwnerId',
    'companyInfo',
    'contact',
    'source',
    'date',
    'lastUpdated',
    'status',
    'notes',
    'edit'
  ];
  defaultSort = {
    direction: 'desc',
    sortBy: 'date'
  };
  sort = {
    direction: SortDirection[this.defaultSort.direction],
    sortBy: SortLeads[this.defaultSort.sortBy]
  };
  disabledStatus = LeadStatus.ConvertedToCustomer;

  leadsData: Lead[] = [];
  leadsTotal: number;
  totalPages: number;
  leadsPerPage = 15;
  currentPageItemCount = 0;
  pageOffset = 0;
  maxNoteLength = 60;
  isAccountManager = false;
  amEmployeeId: string;
  accountManagerId: string;
  accountManagers: { value: string, label: string }[] = [];
  statuses: { value: number, label: string }[];
  sources: {
    [key: number]: string;
  };
  loading = false;
  noResults = false;
  loadingTable = true;
  currentLead: Lead;

  leadsForm: any;
  initialFormValues: any;

  leadOwnerConfirmationOpen$ = new EventEmitter<boolean>();
  selectedLeadOwner: string;
  leadOwnerConfirmationButtons = [
    { label: 'No', type: 'secondary', onclick: this.resetToPreviousValues.bind(this) },
    { label: 'Yes', type: 'primary', onclick: this.updateLeadOwner.bind(this) },
  ];

  statusConfirmationOpen$ = new EventEmitter<boolean>();
  selectedStatus: number;
  statusConfirmationButtons = [
    { label: 'No', type: 'secondary', onclick: this.resetToPreviousValues.bind(this) },
    { label: 'Yes', type: 'primary', onclick: this.updateStatus.bind(this) },
  ];

  constructor(
    private appInfoService: AppInfoService,
    private dashboardService: DashboardService,
    private dialogService: DialogService,
    private dialog: MatDialog,
    private utilsService: UtilsService,
    private notesWidgetService: NotesWidgetService,
    private fb: FormBuilder,
    private deviceService: DeviceDetectorService
  ) { }

  ngOnInit() {
    this.appStrings = this.appInfoService.appStrings;
    this.dateFormat = this.appInfoService.dateFormat;
    this.isAccountManager = this.appInfoService.isAccountManager();
    this.amEmployeeId = this.appInfoService.userData.UserInfo.EmployeeId;
    this.dashboardService.getFollowupAccountManagers((list: AccountManager[]) => {
      this.accountManagers = this.utilsService.mapObjectToSelect(list, 'id', 'name');
      list.forEach((item: AccountManager) => {
        if (item.employeeId === this.amEmployeeId) {
          this.accountManagerId = item.id;
        }
      });
      this.dashboardService.getStaticValues((templateValues: DashboardTemplateValues) => {
        this.statuses = this.utilsService.mapNumberKeyValueOptions(templateValues.LeadActionStatus);
        this.sources = templateValues.SourceType;
      });
      this.getLeads();
    });
  }

  getLeads(scrollToTop = false, setLoading = false) {
    if (setLoading) {
      this.loading = true;
    }
    if (scrollToTop) {
      this.scrollIntoView();
    }
    this.dashboardService.getLeads(
      this.isAccountManager ? this.accountManagerId : null,
      this.pageOffset,
      this.leadsPerPage,
      this.sort,
      this.isHistory,
    ).subscribe(result => {
      this.leadsData = result.filteredLeads;
      this.leadsTotal = result.count;
      this.currentPageItemCount = result.filteredLeads?.length ?? 0;
      this.noResults = this.currentPageItemCount === 0;
      this.totalPages = Math.ceil(result.count / this.leadsPerPage);

      this.initFormValues();

      this.loading = false;
      this.loadingTable = false;
    });
  }

  initFormValues() {
    this.leadsForm = this.fb.group({});
    this.leadsData.forEach((item, index) => {
      item.displayNotesInput = !item.hasNote;
      this.leadsForm.addControl(index, this.fb.group({
        leadOwnerId: [item.leadOwnerId],
        status: [{ value: item.status, disabled: item.status === this.disabledStatus }],
        note: '',
      }));
    });
    this.initialFormValues = this.leadsForm.getRawValue();
  }

  openEditLead(index: number) {
    this.currentLead = this.leadsData[index];
    const dialogRef = this.dialog.open(EditLeadDialogComponent, {
      panelClass: ['ds', 'fullWidth', this.deviceService.isMobile() ? 'fullScreen' : ''],
      data: {
        leadId: this.currentLead.leadId,
        contactId: this.currentLead.contactId
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.shouldRefresh) {
        this.getLeads(true, true);
      }
    });
  }

  onLeadOwnerSelect(event$: MatAutocompleteSelectedEvent, index: number) {
    const value = event$.option.value;
    this.selectedLeadOwner = value;
    this.currentLead = this.leadsData[index];
    if (value !== this.currentLead.leadOwnerId) {
      if (this.isAccountManager) {
        this.leadOwnerConfirmationOpen$.emit(true);
      } else {
        this.dashboardService.updateLeadOwner(this.currentLead.leadId, value).subscribe(() => {
          this.currentLead.leadOwnerId = value;
          if (this.currentLead.status === LeadStatus.RejectLead) {
            this.getLeads(true, true);
          }
        });
      }
    }
  }

  updateLeadOwner() {
    this.dashboardService.updateLeadOwner(this.currentLead.leadId, this.selectedLeadOwner).subscribe(() => {
      this.getLeads(true, true);
    });
  }

  onStatusSelect(event$: MatSelectChange, index: number) {
    const value = event$.value;
    this.selectedStatus = value;
    this.currentLead = this.leadsData[index];
    if (this.dashboardService.leadsStatusNeedingNote.includes(this.selectedStatus)) {
      const dialogParams: DialogData = {
        title: this.appStrings.followUp_confirmation_changeStatus_title,
        disableClose: true,
        content: this.appStrings.followUp_confirmation_changeStatus_note_content,
        buttons: [
          { label: 'Back', style: 'secondary' },
          { label: 'Confirm', focused: true, style: 'primary', withValidation: true },
        ]
      };
      const dialogRef = this.dialogService.openNotesDialog(dialogParams);
      dialogRef.afterClosed().subscribe(result => {
        if (result.buttonIndex === 1) {
          this.dashboardService.updateStatus(this.currentLead.leadId, result.value.reason, this.selectedStatus).subscribe(() => {
            this.getLeads(true, true);
          });
        } else {
          this.resetToPreviousValues();
        }
      });
    } else {
      this.statusConfirmationOpen$.emit(true);
    }
  }

  updateStatus() {
    this.dashboardService.updateStatus(this.currentLead.leadId, null, this.selectedStatus).subscribe(() => {
      this.getLeads(true, true);
    });
  }

  onNoteBlur(index: number) {
    const noteControl = this.leadsForm.controls[index].controls.note;
    if (noteControl.value) {
      this.notesWidgetService.saveAccountsNotes(this.leadsData[index].accountId, noteControl.value, true).subscribe(() => {
        this.leadsData[index].noteText = noteControl.value;
        this.leadsData[index].displayNotesInput = false;
        noteControl.reset();
      });
    }
  }

  getLeadOwnerControl(index: number) {
    return this.leadsForm.controls[index].controls.leadOwnerId;
  }

  getStatusControl(index: number) {
    return this.leadsForm.controls[index].controls.status;
  }

  getNotesControl(index: number) {
    return this.leadsForm.controls[index].controls.note;
  }

  displayLeadOwner(accountManager: any) {
    return typeof accountManager === 'string' ? accountManager : accountManager?.name;
  }

  toggleInput(index: number) {
    this.leadsData[index].displayNotesInput = true;
  }

  resetToPreviousValues() {
    this.leadsForm.reset(this.initialFormValues);
  }

  pageChanged($event: PaginationPageEvent) {
    this.loading = true;
    this.pageOffset = ($event.pageNumber - 1) * this.leadsPerPage;
    this.getLeads(true, true);
  }

  sortData($event: Sort) {
    this.loading = true;
    this.sort = {
      direction: SortDirection[$event.direction === '' ? 'none' : $event.direction],
      sortBy: SortLeads[$event.direction === '' ? 'none' : $event.active]
    };
    this.getLeads(true, true);
  }

  truncateNote(text: string) {
    if (text?.length <= this.maxNoteLength) {
      return text;
    }
    return text?.slice(0, this.maxNoteLength) + '...';
  }

  getNoteTooltip(text: string) {
    return text?.length <= this.maxNoteLength ? '' : text;
  }

  getCompanyRedirect(leadId: number) {
    return this.leadDashboard + leadId;
  }

  private scrollIntoView() {
    this.utilsService.scrollToElement(this.leadsTable.nativeElement, 145);
  }
}
